import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFModal from "@/components/Modal";

import {ContractAPI} from "@/api/contract";
import {PlayerAPI} from "@/api/player";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {dateFormat, ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";

import "./index.scss";

// ── Label maps ────────────────────────────────────────────────────────────────

const contractTypeLabels = {
	professional: "Professionistico",
	amateur: "Dilettantistico",
	youth: "Settore giovanile",
	loan: "Prestito",
	amateur_professional: "Semiprofessionistico",
};

const bonusTypeLabels = {
	appearance: "Presenze",
	goal: "Gol",
	assist: "Assist",
	championship: "Titolo/campionato",
	promotion: "Promozione",
	other: "Altro",
};

const frequencyLabels = {
	per_occurrence: "Per occorrenza",
	per_season: "Per stagione",
	one_time: "Una tantum",
};

const currencySymbol = {EUR: "€", USD: "$", GBP: "£"};

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatMoney = (value, currency = "EUR") => {
	if (value == null || value === "") return "—";
	const sym = currencySymbol[currency] || currency;
	return (
		sym +
		" " +
		Number(value).toLocaleString("it-IT", {
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		})
	);
};

const daysToExpiryNode = (days) => {
	if (days === null || days === undefined) return "—";
	if (days < 0) return <span className="ct-expiry ct-expiry--expired">Scaduto</span>;
	if (days <= 30) return <span className="ct-expiry ct-expiry--critical">{days} giorni</span>;
	if (days <= 90) return <span className="ct-expiry ct-expiry--warning">{days} giorni</span>;
	return <span className="ct-expiry ct-expiry--safe">{days} giorni</span>;
};

// ── Sub-component: read-only field row ────────────────────────────────────────

function DetailRow({label, value, wide}) {
	return (
		<div className={"ct-detail-row" + (wide ? " ct-detail-row--wide" : "")}>
			<span className="ct-detail-label">{label}</span>
			<span className="ct-detail-value">{value ?? "—"}</span>
		</div>
	);
}

// ── Page ──────────────────────────────────────────────────────────────────────

function ContractPage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();

	const [loading, setLoading] = useState(false);
	const [contract, setContract] = useState(null);
	const [playerName, setPlayerName] = useState("");
	const [openConfirm, setOpenConfirm] = useState(false);

	const loadContract = async () => {
		setLoading(true);
		try {
			const data = await ContractAPI.getById(id);
			setContract(data);
			if (data.playerId) {
				const player = await PlayerAPI.getById(data.playerId);
				setPlayerName((player.lastName || "") + " " + (player.firstName || ""));
			}
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadContract();
	}, [id]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleDelete = async () => {
		setLoading(true);
		try {
			await ContractAPI.delete(id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/manager/players-contracts");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/manager/players-contracts"),
			color: "info",
		},
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () => navigate(`/manager/players-contracts/${id}/edit`),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => setOpenConfirm(true),
			color: "error",
		},
	];

	const breadcrumbsElements = [
		{label: "Contratti", path: "/manager/players-contracts"},
		{label: "/"},
	];

	const currency = contract?.currency || "EUR";
	const bonuses = contract?.bonuses || [];

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={playerName || "Contratto"}
				actions={<MainActions actions={mainActions} />}
			/>

			{contract && (
				<div className="sheet-container">
					<div className="row g-2 no-margin">

						{/* ── Informazioni principali ─────────────────────────── */}
						<div className="col-sm-12 padding-small">
							<VLFCard
								title="Informazioni principali"
								openable={false}
								content={
									<div className="ct-detail-grid">
										<DetailRow
											label="Giocatore"
											value={
												playerName ? (
													<span
														className="ct-player-link"
														onClick={() => navigate("/players/" + contract.playerId)}
													>
														{playerName}
													</span>
												) : null
											}
										/>
										<DetailRow
											label="Tipologia"
											value={contractTypeLabels[contract.contractType] || contract.contractType}
										/>
										<DetailRow
											label="Data stipula"
											value={contract.signingDate ? moment(contract.signingDate).format(dateFormat) : null}
										/>
										<DetailRow
											label="Data inizio"
											value={contract.startDate ? moment(contract.startDate).format(dateFormat) : null}
										/>
										<DetailRow
											label="Data scadenza"
											value={contract.endDate ? moment(contract.endDate).format(dateFormat) : null}
										/>
										<DetailRow
											label="Scadenza tra"
											value={daysToExpiryNode(contract.daysToExpiry)}
										/>
										<DetailRow
											label="Valuta"
											value={currency}
										/>
										<DetailRow
											label="Stato"
											value={
												<span className={"ct-status-badge ct-status-badge--" + (contract.isActive ? "active" : "inactive")}>
													{contract.isActive ? "Attivo" : "Non attivo"}
												</span>
											}
										/>
									</div>
								}
							/>
						</div>

						{/* ── Informazioni economiche ─────────────────────────── */}
						<div className="col-sm-12 padding-small">
							<VLFCard
								title="Informazioni economiche"
								openable={false}
								content={
									<div className="ct-detail-grid">
										<DetailRow
											label="Costo cartellino"
											value={formatMoney(contract.transferFee, currency)}
										/>
										<DetailRow
											label="Stipendio fisso mensile"
											value={formatMoney(contract.fixedMonthlySalary, currency)}
										/>
										<DetailRow
											label="Totale mensile stimato"
											value={formatMoney(contract.totalMonthlySalary, currency)}
										/>
										<DetailRow
											label="Retribuzione annua lorda"
											value={formatMoney(contract.annualGrossSalary, currency)}
										/>
										<DetailRow
											label="Bonus alla firma"
											value={formatMoney(contract.signingBonus, currency)}
										/>
										<DetailRow
											label="Clausola rescissoria"
											value={formatMoney(contract.releaseClause, currency)}
										/>
									</div>
								}
							/>
						</div>

						{/* ── Bonus ───────────────────────────────────────────── */}
						<div className="col-sm-12 padding-small">
							<VLFCard
								title={`Bonus${bonuses.length ? ` (${bonuses.length})` : ""}`}
								openable={false}
								content={
									bonuses.length === 0 ? (
										<div className="ct-empty">Nessun bonus</div>
									) : (
										<div className="ct-bonuses">
											{bonuses.map((bonus, i) => (
												<div key={bonus.id || i} className="ct-bonus-item">
													<div className="ct-bonus-header">
														<span className="ct-bonus-description">
															{bonus.description || "—"}
														</span>
														<span className="ct-bonus-amount">
															{formatMoney(bonus.amount, currency)}
														</span>
													</div>
													<div className="ct-bonus-meta">
														{bonus.bonusType && (
															<span className="ct-bonus-tag">
																{bonusTypeLabels[bonus.bonusType] || bonus.bonusType}
															</span>
														)}
														{bonus.frequency && (
															<span className="ct-bonus-tag">
																{frequencyLabels[bonus.frequency] || bonus.frequency}
															</span>
														)}
														{bonus.maxOccurrencesPerSeason != null && (
															<span className="ct-bonus-tag">
																Max {bonus.maxOccurrencesPerSeason}×/stagione
															</span>
														)}
													</div>
													{bonus.condition && (
														<div className="ct-bonus-condition">
															<span className="ct-detail-label">Condizione:</span> {bonus.condition}
														</div>
													)}
													{bonus.notes && (
														<div className="ct-bonus-condition">
															<span className="ct-detail-label">Note:</span> {bonus.notes}
														</div>
													)}
												</div>
											))}
										</div>
									)
								}
							/>
						</div>

						{/* ── Note ────────────────────────────────────────────── */}
						{contract.notes && (
							<div className="col-sm-12 padding-small">
								<VLFCard
									title="Note"
									openable={false}
									content={
										<div className="ct-notes">{contract.notes}</div>
									}
								/>
							</div>
						)}
					</div>
				</div>
			)}

			<VLFModal
				open={openConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo contratto?
					</div>
				}
				confirm={handleDelete}
				close={() => setOpenConfirm(false)}
				confirmLabel="Elimina"
			/>
		</Page>
	);
}

export default ContractPage;
